import React, { useState, useEffect, useRef } from 'react';
import { sellerDashboardAPI, sellerProfileAPI } from '../../services/api.service';
import '../styles/MyPlansPage.css';

const COMPANY_DETAILS = {
  name: 'VEDANT INFO EDGE INDIA LLP',
  gstin: '27ABBFV5785B1Z0',
  cin: 'ACV-0022',
  logo: '/finallogo.png',
  website: 'www.360coordinates.com',
  email: 'info@360coordinates.com',
  address:
    'SR NO 62/3/4 TF OFFICE NO 22 ASTONPLAZA AMBEGAON BK KATRAJ POLICE STATION HAVELI Pune, Pune, Maharashtra, India - 411046',
};

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const MyPlansPage = () => {
  const topRef = useRef(null);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [billingProfile, setBillingProfile] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    fetchSubscriptionData();
  }, []);

  const fetchSubscriptionData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [subscriptionResponse, profileResponse] = await Promise.allSettled([
        sellerDashboardAPI.getSubscriptions(),
        sellerProfileAPI.get(),
      ]);

      if (subscriptionResponse.status === 'fulfilled') {
        const response = subscriptionResponse.value;
        if (response.success) {
          setSubscriptionData(response.data);
        } else {
          setError(response.message || 'Failed to load subscription data');
        }
      } else {
        throw subscriptionResponse.reason;
      }

      if (profileResponse.status === 'fulfilled' && profileResponse.value?.success) {
        setBillingProfile(profileResponse.value.data?.profile || {});
      }
    } catch (err) {
      console.error('Error fetching subscription data:', err);
      setError('Failed to load subscription data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)
      .toLocaleString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      })
      .replace(/\b(am|pm)\b/g, (match) => match.toUpperCase());
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const formatInvoiceNumber = (id) => String(Math.max(Number(id) || 1, 1)).padStart(6, '0');

  const getTaxRows = (subscription) => {
    if (subscription.tax_type === 'cgst_sgst') {
      return [
        { label: 'CGST @ 9%', amount: subscription.cgst_rupees || 0 },
        { label: 'SGST @ 9%', amount: subscription.sgst_rupees || 0 },
      ];
    }

    return [{ label: 'IGST @ 18%', amount: subscription.igst_rupees || subscription.gst_rupees || 0 }];
  };

  const getUserProfile = () => {
    try {
      return JSON.parse(localStorage.getItem('userData') || 'null') || {};
    } catch (err) {
      return {};
    }
  };

  const numberToWords = (amount) => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

    const twoDigits = (num) => {
      if (num < 20) return ones[num];
      return `${tens[Math.floor(num / 10)]}${num % 10 ? ` ${ones[num % 10]}` : ''}`.trim();
    };

    const threeDigits = (num) => {
      const hundred = Math.floor(num / 100);
      const rest = num % 100;
      return `${hundred ? `${ones[hundred]} Hundred ` : ''}${rest ? twoDigits(rest) : ''}`.trim();
    };

    const toIndianWords = (num) => {
      if (num === 0) return 'Zero';
      const crore = Math.floor(num / 10000000);
      const lakh = Math.floor((num % 10000000) / 100000);
      const thousand = Math.floor((num % 100000) / 1000);
      const hundred = num % 1000;
      const parts = [];

      if (crore) parts.push(`${twoDigits(crore)} Crore`);
      if (lakh) parts.push(`${twoDigits(lakh)} Lakh`);
      if (thousand) parts.push(`${twoDigits(thousand)} Thousand`);
      if (hundred) parts.push(threeDigits(hundred));

      return parts.join(' ').trim();
    };

    return `${toIndianWords(Math.round(Number(amount || 0)))} INR Only /-`;
  };

  const handleDownloadInvoice = (subscription) => {
    const localUserProfile = getUserProfile();
    const resolvedProfile = { ...localUserProfile, ...billingProfile };
    const invoiceNumber = formatInvoiceNumber(subscription.id);
    const billingName = resolvedProfile.full_name || resolvedProfile.name || 'Customer';
    const billingPhone = resolvedProfile.phone || 'N/A';
    const billingEmail = resolvedProfile.email || 'N/A';
    const billingAddress =
      [
        resolvedProfile.address,
        resolvedProfile.additional_address,
        resolvedProfile.location,
        resolvedProfile.city,
        resolvedProfile.state,
        resolvedProfile.pincode,
      ]
        .filter(Boolean)
        .join(', ') || 'Address not provided';

    const taxRowsHtml = getTaxRows(subscription)
      .map(
        (taxRow) => `
          <tr>
            <td class="tax-row-label">${escapeHtml(taxRow.label)}</td>
            <td class="tax-row-value">${escapeHtml(formatCurrency(taxRow.amount))}</td>
          </tr>
        `
      )
      .join('');

    const termsHtml = `
      <div class="terms-section">
        <div class="muted box-title">TERMS & CONDITIONS</div>
        <div class="terms-list">
          <div class="terms-item"><strong>1. Service Description</strong><br />The invoice is issued for property listing, promotion, advertisement, or related digital services provided on the website/platform.</div>
          <div class="terms-item"><strong>2. No Refund Policy</strong><br />Payments once made are non-refundable, under any circumstances.<br />No refund for unused listing period or early termination.</div>
          <div class="terms-item"><strong>3. Service Validity</strong><br />Property listings or promotional services will remain active for the agreed duration only.<br />Renewal requires fresh payment.</div>
          <div class="terms-item"><strong>4. Client Responsibilities</strong><br />Client must provide accurate property details, images, and legal information.<br />The company is not responsible for incorrect or misleading data provided by the client.</div>
          <div class="terms-item"><strong>5. Content & Legal Compliance</strong><br />Client confirms that the property is legally owned/authorized for listing.<br />Any disputes related to property legality are the sole responsibility of the client.</div>
          <div class="terms-item"><strong>6. Account Suspension</strong><br />The company reserves the right to suspend/remove listings for non-payment, fake or illegal property listings, or violation of platform policies.</div>
          <div class="terms-item"><strong>7. Limitation of Liability</strong><br />The company is not liable for any loss, damages, or failed transactions arising from property deals.<br />The platform acts only as a listing/advertising medium.</div>
          <div class="terms-item"><strong>8. Cancellation Policy</strong><br />Once the service is activated, cancellation is not allowed.<br />Any cancellation request before activation is subject to approval.</div>
          <div class="terms-item"><strong>9. Jurisdiction</strong><br />All disputes shall be subject to the jurisdiction of Pune, Maharashtra courts.</div>
          <div class="terms-item"><strong>10. Force Majeure</strong><br />The company shall not be liable for failure due to events beyond control (server downtime, natural disasters, etc.).</div>
          <div class="terms-item"><strong>11. Pricing Policy </strong><br />Prices are subject to change without prior notice.</div>
          <div class="terms-item"><strong>12. Amendments</strong><br />Terms & conditions may be updated without prior notice.</div>
        </div>
      </div>
    `;

    const invoiceHtml = `
      <!DOCTYPE html>
      <html lang="en">
        <head>
          <meta charset="UTF-8" />
          <title></title>
          <style>
            @page { size: A4; margin: 10mm;border: 1px solid rgba(0, 0, 0, 0.35);  }
            * { box-sizing: border-box; }
            body { font-family: Arial, sans-serif; background: #f8fafc; color: #0f172a; margin: 0; padding: 10px;  }
            .invoice { width: 190mm; max-width: 190mm; margin: 0 auto; background: #ffffff; padding: 14mm; padding-bottom: 34mm;  }
            .top { display:block; margin-bottom:14px; }
            .company { display:flex; align-items:flex-start; justify-content:space-between; gap:16px; min-width: 0; }
            .company-details { flex:1; min-width:0; text-align:right; }
            .company h1 { margin:0; font-size:22px; line-height:1.2; }
            .company-logo { width: 118px; height: auto;margin-top:-20px; display:block; flex-shrink:0; }
            .muted { color:#64748b; font-size:12px; line-height:1.35; }
            .invoice-title { margin-top: 12px; font-size: 22px; font-weight: 700; }
            .invoice-subtitle { margin-top: 6px; }
            .grid { display:grid; grid-template-columns:1fr 1fr; gap:16px; margin-bottom:14px; }
            .box { border-top:1px solid rgba(0, 0, 0, 0.35); padding-top:10px; min-height: 86px; }
            .box-title { font-weight:700; letter-spacing:0.04em; text-transform:uppercase; }
            .box-content { margin-top:8px; }
            .box-content-strong { margin-top:8px; font-weight:700; }
            .date-grid { margin-top: 0; }
            table { width:100%; border-collapse:collapse; margin-top:6px; }
            th { background:#f8fafc; color:#475569; font-size:12px; text-align:left; padding:7px 8px; }
            td { padding:7px 8px; border-bottom:1px solid rgba(0, 0, 0, 0.35); vertical-align:top; font-size:12px; line-height:1.35; }
            .right { text-align:right; }
            .description-cell { width: 52%; }
            .line-description-title { font-weight:700; }
            .line-description-text { margin-top:6px; }
            .tax-row-label { color:#475569; }
            .tax-row-value { text-align:right; color:#0f172a; }
            .taxname{font-weight:700;}
            .totals { width:280px; margin-left:auto; margin-top:12px; }
            .totals td { padding:5px 0; border-bottom:none; font-size:12px; }
            .total-amount td { font-size:20px; font-weight:700; }
            .terms-section { margin-top:18px; padding-top:10px; border-top:1px solid rgba(0, 0, 0, 0.35); page-break-before: always; break-before: page; }
            .terms-list { margin-top:8px; }
            .terms-item { font-size:11px; line-height:1.45; color:#334155; margin-bottom:9px; }
            .print-footer { border-top:1px solid rgba(0, 0, 0, 0.35); padding-top:8px; margin-top:18px; text-align:center; }
            @media print {
              body { background:#fff; padding:0; }
              .invoice { width: 100%; max-width: 100%; margin: 0; padding: 12mm 12mm 24mm 12mm;  }
              .print-footer {
                position: fixed;
                left: 12mm;
                right: 12mm;
                bottom: 8mm;
                background: #fff;
              }
            }
              
          </style>
        </head>
        <body>
          <div class="invoice">
            <div class="top">
              <div class="company">
                <img src="${escapeHtml(COMPANY_DETAILS.logo)}" alt="360Coordinates Logo" class="company-logo" />
                <div class="company-details">
                  <h1>${escapeHtml(COMPANY_DETAILS.name)}</h1>
                  <div class="muted">GSTIN - ${escapeHtml(COMPANY_DETAILS.gstin)}</div>
                  <div class="muted">CIN - ${escapeHtml(COMPANY_DETAILS.cin)}</div>
                  <div class="muted">${escapeHtml(COMPANY_DETAILS.website)}</div>
                  <div class="muted">${escapeHtml(COMPANY_DETAILS.email)}</div>
                </div>
              </div>
              <div class="invoice-title">Invoice # ${escapeHtml(invoiceNumber)}</div>
              <div class="muted invoice-subtitle">Subscription invoice for property display plan</div>
            </div>

            <div class="grid">
              <div class="box">
                <div class="muted box-title">BILLING TO</div>
                <div class="box-content-strong">${escapeHtml(billingName)}</div>
                <div class="muted">${escapeHtml(billingPhone)}</div>
                <div class="muted">${escapeHtml(billingEmail)}</div>
              </div>
              <div class="box">
                <div class="muted box-title">BILLING ADDRESS</div>
                <div class="box-content">${escapeHtml(billingAddress)}</div>
              </div>
            </div>

            <div class="grid date-grid">
              <div class="box">
                <div class="muted box-title">ISSUE DATE</div>
                <div class="box-content">${escapeHtml(formatDate(subscription.created_at))}</div>
              </div>
              <div class="box">
                <div class="muted box-title">PLACE OF SUPPLY</div>
                <div class="box-content">${escapeHtml(subscription.place_of_supply || 'Not provided')}</div>
              </div>
            </div>

            <table>
              <thead>
                <tr>
                  <th>DESCRIPTION</th>
                  <th class="right">RATE/ITEM</th>
                  <th class="right">QTY</th>
                  <th class="right">TOTAL</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="description-cell">
                    <div class="line-description-title">PROPERTY FOR DISPLAY</div>
                    <div class="muted line-description-text">
                      ${escapeHtml(subscription.plan_name.toUpperCase())} FOR ${escapeHtml(String(subscription.duration_months))} MONTH${Number(subscription.duration_months) > 1 ? 'S' : ''}<br />
                      SAC  CODE :  9972
                    </div>
                  </td>
                  <td class="right">${escapeHtml(formatCurrency(subscription.base_rupees))}</td>
                  <td class="right">${escapeHtml(String(subscription.properties_limit || 1))}</td>
                  <td class="right">${escapeHtml(formatCurrency(subscription.base_rupees))}</td>
                </tr>
               
              </tbody>
            </table>

            <table class="totals">
              <tr>
                <td>Sub Total</td>
                <td class="right">${escapeHtml(formatCurrency(subscription.base_rupees))}</td>
              </tr>
              <tr>
                
                <td class="right taxname">
  ${taxRowsHtml}
</td>

              </tr>
              <tr class="total-amount">
                <td>Total Amount</td>
                <td class="right">${escapeHtml(formatCurrency(subscription.amount_rupees))}</td>
              </tr>
              <tr>
                <td colspan="2" class="right muted">(${escapeHtml(numberToWords(subscription.amount_rupees))})</td>
              </tr>
            </table>

            ${termsHtml}

            <div class="print-footer">
              <div class="box-content-strong">${escapeHtml(COMPANY_DETAILS.name)}</div>
              <div class="muted box-content">${escapeHtml(COMPANY_DETAILS.address)}</div>
            </div>
          </div>
          <script>
            window.onload = function () {
              document.title = '';
              window.print();
            };
          </script>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank', 'width=1024,height=768');
    if (!printWindow) {
      window.alert('Please allow pop-ups to download the invoice.');
      return;
    }

    printWindow.document.open();
    printWindow.document.write(invoiceHtml);
    printWindow.document.close();
  };

  const getStatusBadge = (status, daysRemaining) => {
    if (status !== 'active') {
      return <span className="mp-status-badge-myplans inactive">{status}</span>;
    }

    if (daysRemaining === null) {
      return <span className="mp-status-badge-myplans active">Active</span>;
    }

    if (daysRemaining === 0) {
      return <span className="mp-status-badge-myplans expired">Expired</span>;
    }

    if (daysRemaining <= 7) {
      return <span className="mp-status-badge-myplans urgent">Active ({daysRemaining + 1} days left)</span>;
    }

    return <span className="mp-status-badge-myplans active">Active ({daysRemaining + 1} days left)</span>;
  };

  if (loading) {
    return (
      <div className="mp-page-myplans">
        <div className="mp-container-myplans">
          <div className="loading-spinner-myplans">
            <div className="spinner-myplans"></div>
            <p>Loading your plans...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mp-page-myplans">
        <div className="mp-container-myplans">
          <div className="mp-error-message-myplans">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
              <path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <h3>Error Loading Plans</h3>
            <p>{error}</p>
            <button onClick={fetchSubscriptionData} className="mp-retry-btn-myplans">
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!subscriptionData || subscriptionData.subscriptions.length === 0) {
    return (
      <div className="mp-page-myplans">
        <div className="mp-container-myplans">
          <header className="mp-header-myplans">
            <h1>My Plans & Payment History</h1>
            <p>View your subscription plans and payment history</p>
          </header>

          <div className="mp-empty-state-myplans">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <h3>No Plans Found</h3>
            <p>You haven't purchased any plans yet.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mp-page-myplans" ref={topRef}>
      <div className="mp-container-myplans">
        <header className="mp-header-myplans">
          <h1>My Plans & Payment History</h1>
          <p>View your subscription plans and payment history</p>
        </header>

        <div className="mp-summary-cards-myplans">
          <div className="mp-summary-card-myplans">
            <div className="mp-summary-icon-myplans">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="mp-summary-content-myplans">
              <h3>{subscriptionData.summary.total_plans}</h3>
              <p>Total Plans</p>
            </div>
          </div>

          <div className="mp-summary-card-myplans">
            <div className="mp-summary-icon-myplans">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
                <path d="M9 9h6v6H9z" stroke="currentColor" strokeWidth="2" />
              </svg>
            </div>
            <div className="mp-summary-content-myplans">
              <h3>{subscriptionData.summary.active_plans}</h3>
              <p>Active Plans</p>
            </div>
          </div>

          <div className="mp-summary-card-myplans">
            <div className="mp-summary-icon-myplans">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M7 5H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M7 9H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M9 5C9 8.2 10.9 9.8 14 9.8H7L15.5 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="mp-summary-content-myplans">
              <h3>{formatCurrency(subscriptionData.summary.total_spent_rupees)}</h3>
              <p>Total Spent</p>
            </div>
          </div>
        </div>

        <div className="mp-plans-list-myplans">
          <h2>Your Subscription History</h2>
          <div className="mp-table-container-myplans">
            <table className="mp-table-myplans">
              <thead>
                <tr>
                  <th>Plan Details</th>
                  <th>Payment Info</th>
                  <th>Status</th>
                  <th>Validity</th>
                  <th>Payment Date</th>
                  <th>Invoice</th>
                </tr>
              </thead>
              <tbody>
                {subscriptionData.subscriptions.map((subscription) => (
                  <tr key={subscription.id}>
                    <td className="mp-plan-details-myplans">
                      <div className="mp-plan-name-myplans">{subscription.plan_name}</div>
                      <div className="mp-plan-meta-myplans">
                        {subscription.properties_limit} property | {subscription.duration_months} months
                      </div>
                    </td>
                    <td className="mp-payment-info-myplans">
                      <div className="mp-payment-amount-myplans">{formatCurrency(subscription.amount_rupees)}</div>
                      <div className="mp-payment-breakdown-myplans">
                        Base: {formatCurrency(subscription.base_rupees)}
                        <br />
                        {subscription.tax_type === 'cgst_sgst'
                          ? `CGST: ${formatCurrency(subscription.cgst_rupees)} | SGST: ${formatCurrency(subscription.sgst_rupees)}`
                          : `IGST: ${formatCurrency(subscription.igst_rupees || subscription.gst_rupees)}`}
                      </div>
                      <div className="mp-payment-tax-type-myplans">Place of Supply: {subscription.place_of_supply || 'N/A'}</div>
                      <div className="mp-payment-id-myplans">ID: {subscription.razorpay_payment_id}</div>
                    </td>
                    <td className="mp-status-cell-myplans">{getStatusBadge(subscription.status, subscription.days_remaining)}</td>
                    <td className="mp-validity-cell-myplans">
                      <div className="mp-validity-dates-myplans">
                        <div>From: {formatDate(subscription.starts_at)}</div>
                        <div>To: {subscription.ends_at ? formatDate(subscription.ends_at) : 'Ongoing'}</div>
                      </div>
                    </td>
                    <td className="mp-payment-date-myplans">{formatDate(subscription.created_at)}</td>
                    <td className="mp-invoice-cell-myplans">
                      <button type="button" className="mp-invoice-btn-myplans" onClick={() => handleDownloadInvoice(subscription)}>
                        Download Invoice
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyPlansPage;
